import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Grid, Card, CardContent, Chip,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Button, Divider, TextField, Alert, Checkbox, FormControlLabel,
  Autocomplete, Stack
} from '@mui/material';
import {
  Assessment, Download, Print, Refresh, AccessTime,
  CheckCircle, Cancel, EventNote, TrendingUp, TrendingDown,
  People, GetApp, FilterList, Clear,
  BarChart as BarChartIcon, PieChart as PieChartIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area
} from 'recharts';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const AttendanceReports = () => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [summary, setSummary] = useState(null);

  const [reportType, setReportType] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [chartView, setChartView] = useState('bar');

  const COLORS = ['#4caf50', '#f44336', '#ff9800', '#2196f3', '#9c27b0', '#ff5722', '#00bcd4'];

  const STATUS_COLORS = {
    present: '#4caf50',
    absent: '#f44336',
    leave: '#ff9800',
    half_day: '#2196f3',
    holiday: '#9c27b0',
    weekend: '#00bcd4',
    late: '#ff5722'
  };

  const STATUS_LABELS = {
    present: 'Present',
    absent: 'Absent',
    leave: 'Leave',
    half_day: 'Half Day',
    holiday: 'Holiday',
    weekend: 'Weekend',
    late: 'Late'
  };

  useEffect(() => {
    fetchEmployees();
    fetchShifts();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/hr/employees/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setEmployees(data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  };

  const fetchShifts = async () => {
    try {
      const response = await api.get('/hr/shifts/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setShifts(data.filter(s => s.is_active));
    } catch (error) {
      toast.error('Failed to fetch shifts');
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (reportType === 'daily' && startDate) {
        params.append('start_date', startDate);
        params.append('end_date', startDate);
      } else if (reportType === 'between' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      } else if (reportType === 'monthly') {
        params.append('month', selectedMonth);
        params.append('year', selectedYear);
      } else if (reportType === 'yearly') {
        params.append('year', selectedYear);
      }

      if (selectedEmployees.length > 0) {
        selectedEmployees.forEach(empId => {
          params.append('employee_ids', empId);
        });
      }

      if (selectedShifts.length > 0) {
        selectedShifts.forEach(shiftId => {
          params.append('shift_ids', shiftId);
        });
      }

      const url = `/hr/attendance/report/?${params.toString()}`;
      
      const response = await api.get(url);
      
      if (response.data) {
        setAttendanceData(response.data.records || []);
        setSummary(response.data.summary || null);
        
        if (response.data.records?.length === 0) {
          toast('No attendance records found for the selected filters', {
            icon: 'ℹ️',
            duration: 3000
          });
        } else {
          toast.success(`Found ${response.data.records.length} attendance records`);
        }
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to fetch report data');
      setAttendanceData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? (emp.full_name || emp.employee_id) : 'N/A';
  };

  const getShiftName = (shiftId) => {
    const shift = shifts.find(s => s.id === shiftId);
    return shift ? shift.name : 'N/A';
  };

  const getStatusDistributionData = () => {
    if (!summary) return [];
    return [
      { name: 'Present', value: summary.total_present || 0, color: '#4caf50' },
      { name: 'Absent', value: summary.total_absent || 0, color: '#f44336' },
      { name: 'Leave', value: summary.total_leave || 0, color: '#ff9800' },
      { name: 'Half Day', value: summary.total_half_day || 0, color: '#2196f3' },
      { name: 'Holiday', value: summary.total_holiday || 0, color: '#9c27b0' },
      { name: 'Weekend', value: summary.total_weekend || 0, color: '#00bcd4' }
    ];
  };

  const getDailyStatusData = () => {
    const statusCounts = {};
    attendanceData.forEach(record => {
      const date = new Date(record.date).toLocaleDateString('en-IN');
      if (!statusCounts[date]) {
        statusCounts[date] = { date, present: 0, absent: 0, leave: 0, half_day: 0 };
      }
      const status = record.status || 'present';
      if (statusCounts[date][status] !== undefined) {
        statusCounts[date][status]++;
      }
    });
    return Object.values(statusCounts).slice(-10);
  };

  const getShiftWiseData = () => {
    const shiftCounts = {};
    attendanceData.forEach(record => {
      const shiftName = getShiftName(record.shift);
      if (!shiftCounts[shiftName]) {
        shiftCounts[shiftName] = { name: shiftName, present: 0, absent: 0, leave: 0 };
      }
      const status = record.status || 'present';
      if (shiftCounts[shiftName][status] !== undefined) {
        shiftCounts[shiftName][status]++;
      }
    });
    return Object.values(shiftCounts);
  };

  const getEmployeeWiseData = () => {
    const empCounts = {};
    attendanceData.forEach(record => {
      const empName = getEmployeeName(record.employee);
      if (!empCounts[empName]) {
        empCounts[empName] = { name: empName, present: 0, absent: 0, leave: 0 };
      }
      const status = record.status || 'present';
      if (empCounts[empName][status] !== undefined) {
        empCounts[empName][status]++;
      }
    });
    return Object.values(empCounts).slice(0, 10);
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const reportTypes = [
    { value: 'daily', label: 'Daily Report' },
    { value: 'between', label: 'Between Dates' },
    { value: 'monthly', label: 'Monthly Report' },
    { value: 'yearly', label: 'Yearly Report' }
  ];

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="subtitle2">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ bgcolor: color + '20', borderRadius: '50%', p: 1.5 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const handleExportCSV = () => {
    if (attendanceData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Employee', 'Date', 'Shift', 'Status', 'Check In', 'Check Out', 'Overtime (hrs)', 'Late (min)', 'Early Leave (min)'];
    const rows = attendanceData.map(record => [
      getEmployeeName(record.employee),
      new Date(record.date).toLocaleDateString('en-IN'),
      getShiftName(record.shift),
      STATUS_LABELS[record.status] || record.status,
      record.check_in_time || '-',
      record.check_out_time || '-',
      record.overtime_hours || 0,
      record.late_minutes || 0,
      record.early_leave_minutes || 0
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_report_${reportType}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClearFilters = () => {
    setSelectedEmployees([]);
    setSelectedShifts([]);
    setStartDate('');
    setEndDate('');
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
    setAttendanceData([]);
    setSummary(null);
    toast.success('Filters cleared');
  };

  const handleSelectAllEmployees = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  const handleSelectAllShifts = () => {
    if (selectedShifts.length === shifts.length) {
      setSelectedShifts([]);
    } else {
      setSelectedShifts(shifts.map(s => s.id));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
          Attendance Reports
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchReportData}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleExportCSV}
            sx={{ mr: 1 }}
            disabled={attendanceData.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{ mr: 1 }}
            disabled={attendanceData.length === 0}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Clear />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
            Report Filters
          </Typography>
          <Button size="small" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>

        {showFilters && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  label="Report Type"
                >
                  {reportTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {reportType === 'daily' && (
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {reportType === 'between' && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            {reportType === 'monthly' && (
              <>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      label="Month"
                    >
                      {months.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      label="Year"
                    >
                      {years.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {reportType === 'yearly' && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    label="Year"
                  >
                    {years.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Employees</InputLabel>
                <Select
                  multiple
                  value={selectedEmployees}
                  onChange={(e) => setSelectedEmployees(e.target.value)}
                  label="Employees"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={getEmployeeName(value)} 
                          size="small"
                          sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="select_all" key="select_all">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedEmployees.length === employees.length && employees.length > 0}
                          indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                          onChange={handleSelectAllEmployees}
                        />
                      }
                      label="Select All Employees"
                    />
                  </MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      <Checkbox checked={selectedEmployees.includes(emp.id)} />
                      {emp.full_name || emp.employee_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Shifts</InputLabel>
                <Select
                  multiple
                  value={selectedShifts}
                  onChange={(e) => setSelectedShifts(e.target.value)}
                  label="Shifts"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={getShiftName(value)} 
                          size="small"
                          sx={{ backgroundColor: '#fce4ec', color: '#d32f2f' }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="select_all" key="select_all_shifts">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedShifts.length === shifts.length && shifts.length > 0}
                          indeterminate={selectedShifts.length > 0 && selectedShifts.length < shifts.length}
                          onChange={handleSelectAllShifts}
                        />
                      }
                      label="Select All Shifts"
                    />
                  </MenuItem>
                  {shifts.map((shift) => (
                    <MenuItem key={shift.id} value={shift.id}>
                      <Checkbox checked={selectedShifts.includes(shift.id)} />
                      {shift.name} ({shift.start_time} - {shift.end_time})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchReportData}
                disabled={loading}
                sx={{ height: 40 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Chart View Selector */}
      {summary && attendanceData.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2">Chart View:</Typography>
          <Button
            variant={chartView === 'bar' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setChartView('bar')}
            startIcon={<BarChartIcon />}
          >
            Bar Chart
          </Button>
          <Button
            variant={chartView === 'pie' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setChartView('pie')}
            startIcon={<PieChartIcon />}
          >
            Pie Chart
          </Button>
          <Button
            variant={chartView === 'line' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setChartView('line')}
            startIcon={<TrendingUp />}
          >
            Line Chart
          </Button>
        </Box>
      )}

      {/* Charts Section */}
      {summary && attendanceData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Status Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  {chartView === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={getStatusDistributionData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {getStatusDistributionData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  ) : (
                    <BarChart data={getStatusDistributionData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8884d8">
                        {getStatusDistributionData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Daily Status Trend
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  {chartView === 'line' ? (
                    <LineChart data={getDailyStatusData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="present" stroke="#4caf50" />
                      <Line type="monotone" dataKey="absent" stroke="#f44336" />
                      <Line type="monotone" dataKey="leave" stroke="#ff9800" />
                    </LineChart>
                  ) : (
                    <BarChart data={getDailyStatusData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="present" stackId="a" fill="#4caf50" />
                      <Bar dataKey="absent" stackId="a" fill="#f44336" />
                      <Bar dataKey="leave" stackId="a" fill="#ff9800" />
                      <Bar dataKey="half_day" stackId="a" fill="#2196f3" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Shift-wise Analysis
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getShiftWiseData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="present" fill="#4caf50" />
                    <Bar dataKey="absent" fill="#f44336" />
                    <Bar dataKey="leave" fill="#ff9800" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Employee-wise Analysis (Top 10)
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getEmployeeWiseData()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="present" fill="#4caf50" />
                    <Bar dataKey="absent" fill="#f44336" />
                    <Bar dataKey="leave" fill="#ff9800" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Present"
              value={summary.total_present || 0}
              icon={<CheckCircle sx={{ color: '#2e7d32' }} />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Absent"
              value={summary.total_absent || 0}
              icon={<Cancel sx={{ color: '#d32f2f' }} />}
              color="#d32f2f"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Leave"
              value={summary.total_leave || 0}
              icon={<EventNote sx={{ color: '#ed6c02' }} />}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Half Day"
              value={summary.total_half_day || 0}
              icon={<TrendingDown sx={{ color: '#0288d1' }} />}
              color="#0288d1"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Overtime"
              value={`${summary.total_overtime_hours || 0}h`}
              icon={<AccessTime sx={{ color: '#1976d2' }} />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Total Days"
              value={summary.total_days || 0}
              icon={<People sx={{ color: '#9c27b0' }} />}
              color="#9c27b0"
            />
          </Grid>
        </Grid>
      )}

      {/* Detailed Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Attendance Details
          <Chip 
            label={`${attendanceData.length} records`} 
            size="small" 
            sx={{ ml: 2, backgroundColor: '#e3f2fd', color: '#1976d2' }}
          />
          {selectedEmployees.length > 0 && (
            <Chip 
              label={`${selectedEmployees.length} Employee(s)`} 
              size="small" 
              variant="outlined"
              sx={{ ml: 1, borderColor: '#1976d2', color: '#1976d2' }}
            />
          )}
          {selectedShifts.length > 0 && (
            <Chip 
              label={`${selectedShifts.length} Shift(s)`} 
              size="small" 
              variant="outlined"
              sx={{ ml: 1, borderColor: '#d32f2f', color: '#d32f2f' }}
            />
          )}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Check In</TableCell>
                <TableCell>Check Out</TableCell>
                <TableCell align="right">Overtime</TableCell>
                <TableCell align="right">Late</TableCell>
                <TableCell align="right">Early Leave</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : attendanceData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="textSecondary">
                      No attendance records found. Select filters and click "Generate Report".
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                attendanceData.map((record, index) => (
                  <TableRow key={record.id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{getEmployeeName(record.employee)}</TableCell>
                    <TableCell>{new Date(record.date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getShiftName(record.shift)}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#1976d2', color: '#1976d2' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={STATUS_LABELS[record.status] || record.status}
                        size="small"
                        sx={{
                          backgroundColor: STATUS_COLORS[record.status] || '#e0e0e0',
                          color: '#ffffff'
                        }}
                      />
                    </TableCell>
                    <TableCell>{record.check_in_time || '-'}</TableCell>
                    <TableCell>{record.check_out_time || '-'}</TableCell>
                    <TableCell align="right">{record.overtime_hours || 0}</TableCell>
                    <TableCell align="right">{record.late_minutes || 0}</TableCell>
                    <TableCell align="right">{record.early_leave_minutes || 0}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AttendanceReports;